import {
    useQuery,
    useMutation,
    type UseQueryOptions,
    type UseMutationOptions,
} from "@tanstack/react-query";
import { useUserRoles } from "@/hooks/use-user-roles";

export function useHasRoles(...roles: string[]) {
    const { hasAllRoles } = useUserRoles();
    return roles.length === 0 || hasAllRoles(...roles);
}

export function useRoleGatedQuery<
    TQueryFnData = unknown,
    TError = Error,
    TData = TQueryFnData,
    TQueryKey extends readonly unknown[] = readonly unknown[],
>(
    requiredRoles: string[],
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) {
    const hasAccess = useHasRoles(...requiredRoles);
    return useQuery({
        ...options,
        enabled: hasAccess && (options.enabled ?? true),
    });
}

export function useRoleGatedMutation<
    TData = unknown,
    TError = Error,
    TVariables = void,
    TContext = unknown,
>(
    requiredRoles: string[],
    options: UseMutationOptions<TData, TError, TVariables, TContext>,
) {
    const hasAccess = useHasRoles(...requiredRoles);
    return useMutation(
        hasAccess
            ? options
            : {
                  mutationFn: () => Promise.resolve(undefined as TData),
              },
    );
}
